import React, { Fragment } from 'react';
import { StopIcon } from '@heroicons/react/16/solid';
import Utilities from '@util/Utilities';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import Domain from '@app/model/_common/Domain';
import { useDomainTransitionContext } from '@app/context/DomainTransitionContext';
import useSecurity from '@util/SecurityHook';
import { useBiAccess } from '@app/site/secured/bi/BiScopeHooks';


const DomainSelectorFragment = React.memo(() => {
	const { companyHotel } = useSelectedCompanyHotel();
	const navigate = useNavigate();
	const { startTransition } = useDomainTransitionContext();
	const security = useSecurity();

	const hasRateshopperEnabled = security.hasCompanyHotelDomainAuthority(companyHotel, Domain.RATESHOPPER) && companyHotel.hasRateShopperDomain();
	const hasReviewManagerEnabled = security.hasCompanyHotelDomainAuthority(companyHotel, Domain.REVIEW_MANAGER) && companyHotel.hasReviewManagerDomain();
	const hasRmsEnabled = security.hasRmsAccess(companyHotel) && companyHotel.hasRmsDomain();
	// ROO2-274: BI with a BI right on any property; it opens on the open hotel only when that hotel is in the BI scope.
	const biAccess = useBiAccess();
	const hasBiToolEnabled = biAccess.state === 'yes';

	const handleDomainNavigation = (path: string) => {
		startTransition();
		navigate(path);
	};

	return <Menu as="div" className="relative">
		<MenuButton type="button"
								className="btn outline-0 ring-0 rounded-lg p-1.5 mr-2 active:bg-gray-100 hover:bg-gray-100 focus:ring-0 cursor-pointer">
			<img
				className="block h-5 w-auto cursor-pointer"
				src="/assets/img/grid-dots.svg"
				alt="RoomPulse"
			/>
		</MenuButton>
		<Transition
			as={Fragment}
			enter="transition ease-out duration-100"
			enterFrom="transform opacity-0 scale-95"
			enterTo="transform opacity-100 scale-100"
			leave="transition ease-in duration-75"
			leaveFrom="transform opacity-100 scale-100"
			leaveTo="transform opacity-0 scale-95"
		>
			<MenuItems
				className="absolute left-0 z-10 mt-3 w-56 origin-top-right overflow-hidden rounded-md bg-white divide-y divide-gray-100 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
				<div className="px-4 py-3">
					<p className="text-[10px] text-blue-700 font-black uppercase tracking-wide">Your Apps</p>
				</div>
				<div className="py-1">
					<MenuItem>
						{({ focus }) => (
							<button
								onClick={() => handleDomainNavigation('/secured/dashboard')}
								disabled={!hasRateshopperEnabled}
								className={Utilities.classNames(
									focus ? 'bg-gray-50 text-blue-500' : (hasRateshopperEnabled ? 'text-gray-700 hover:bg-gray-50 hover:text-blue-500' : 'opacity-20 pointer-events-none'),
									'group flex justify-between items-center rounded-md px-4 py-2 text-sm font-semibold leading-6 w-full text-left',
								)}
							>
								<div className="flex flex-shrink-0 items-center space-x-2">
									<StopIcon className="w-5 h-5 p-1 bg-gray-100 text-blue-600 rounded-md relative bottom-[1px]" />
									<span>Rateshopper</span>
								</div>
							</button>
						)}
					</MenuItem>
					<MenuItem>
						{({ focus }) => (
							<button
								onClick={() => handleDomainNavigation('/secured/review-manager/inbox')}
								disabled={!hasReviewManagerEnabled}
								className={Utilities.classNames(
									focus ? 'bg-gray-50 text-blue-500' : (hasReviewManagerEnabled ? 'text-gray-700 hover:bg-gray-50 hover:text-blue-500' : 'opacity-20 pointer-events-none'),
									'group flex justify-between items-center rounded-md px-4 py-2 text-sm font-semibold leading-6 w-full text-left',
								)}
							>
								<div className="flex flex-shrink-0 items-center space-x-1">
									<StopIcon className="w-5 h-5 p-1 bg-gray-100 text-blue-600 rounded-md relative bottom-[1px]" />
									<span>Review Manager</span>
								</div>
							</button>
						)}
					</MenuItem>
					{hasRmsEnabled && <MenuItem>
						{({ focus }) => (
							<button
								onClick={() => handleDomainNavigation('/secured/rms/dashboard')}
								className={Utilities.classNames(
									focus ? 'bg-gray-50 text-blue-500' : 'text-gray-700 hover:bg-gray-50 hover:text-blue-500',
									'group flex items-center rounded-md px-4 py-2 text-sm font-semibold leading-6 w-full text-left',
								)}
							>
								<StopIcon className="mr-2 h-5 w-5 rounded-md bg-gray-100 p-1 text-blue-600" />
								<span>RMS</span>
							</button>
						)}
					</MenuItem>}
					{hasBiToolEnabled && <MenuItem>
						{({ focus }) => (
							<button
								onClick={() => handleDomainNavigation(`/secured/bi/segmentation${biAccess.openHotel ? `?hotel=${companyHotel.id}` : ''}`)}
								className={Utilities.classNames(
									focus ? 'bg-gray-50 text-blue-500' : 'text-gray-700 hover:bg-gray-50 hover:text-blue-500',
									'group flex items-center rounded-md px-4 py-2 text-sm font-semibold leading-6 w-full text-left',
								)}
							>
								<StopIcon className="mr-2 h-5 w-5 rounded-md bg-gray-100 p-1 text-blue-600" />
								<span>BI</span>
							</button>
						)}
					</MenuItem>}
				</div>
			</MenuItems>
		</Transition>
	</Menu>;
});

export default DomainSelectorFragment;
